import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import {
  BOOKING_CHANNELS,
  BOOKING_STATUSES,
  type BookingChannel,
  type BookingStatus,
} from "./status";
import type { BookingFilter } from "./searchParams";
import { fieldLabel, secondaryButton, textInput } from "./styles";

/**
 * The shared filter bar (R6 channel filter from the canonical enum; wide-range narrowing prompt lives
 * in the list view). Every change rewrites URL state via [onChange], so the filter is deep-linkable
 * and shared across all four views — switching view keeps the active filters.
 */
export function FilterBar({
  filter,
  onChange,
}: {
  filter: BookingFilter;
  onChange: (next: BookingFilter) => void;
}) {
  const { t } = useTranslation();

  const toggleStatus = (s: BookingStatus) =>
    onChange({
      ...filter,
      status: filter.status.includes(s)
        ? filter.status.filter((x) => x !== s)
        : [...filter.status, s],
    });
  const toggleChannel = (c: BookingChannel) =>
    onChange({
      ...filter,
      channel: filter.channel.includes(c)
        ? filter.channel.filter((x) => x !== c)
        : [...filter.channel, c],
    });

  // Status/channel/party-size are power-user narrowing — hide them behind a disclosure so the bar ships
  // as just search + phone + date range. Auto-open (and badge the count) when any is active so an applied
  // filter is never invisible.
  const advancedCount =
    filter.status.length +
    filter.channel.length +
    (typeof filter.partyMin === "number" ? 1 : 0) +
    (typeof filter.partyMax === "number" ? 1 : 0);

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "3",
        p: "3",
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "lg",
        bg: "bg.subtle",
      })}
    >
      <div className={css({ display: "flex", gap: "3", flexWrap: "wrap" })}>
        <label className={`${fieldLabel} ${css({ flex: "1", minW: "48" })}`}>
          {t("bookings.filter.search")}
          <input
            className={textInput}
            value={filter.q ?? ""}
            placeholder={t("bookings.filter.searchPlaceholder")}
            onChange={(e) => onChange({ ...filter, q: e.target.value || undefined })}
          />
        </label>
        <label className={fieldLabel}>
          {t("bookings.filter.phone")}
          <input
            className={textInput}
            inputMode="tel"
            value={filter.phone ?? ""}
            onChange={(e) => onChange({ ...filter, phone: e.target.value || undefined })}
          />
        </label>
        <label className={fieldLabel}>
          {t("bookings.filter.from")}
          <input
            type="date"
            className={textInput}
            value={filter.from ?? ""}
            onChange={(e) => onChange({ ...filter, from: e.target.value || undefined })}
          />
        </label>
        <label className={fieldLabel}>
          {t("bookings.filter.to")}
          <input
            type="date"
            className={textInput}
            value={filter.to ?? ""}
            onChange={(e) => onChange({ ...filter, to: e.target.value || undefined })}
          />
        </label>
      </div>

      <details open={advancedCount > 0}>
        <summary className={css({ cursor: "pointer", fontSize: "sm", color: "fg.muted", py: "1" })}>
          {advancedCount > 0
            ? t("bookings.filter.moreActive", { count: advancedCount })
            : t("bookings.filter.more")}
        </summary>
        <div className={css({ display: "flex", flexDirection: "column", gap: "3", pt: "3" })}>
          <fieldset
            className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}
          >
            <legend className={css({ fontSize: "xs", color: "fg.muted", pr: "2" })}>
              {t("bookings.field.status")}
            </legend>
            {BOOKING_STATUSES.map((s) => (
              <Chip
                key={s}
                active={filter.status.includes(s)}
                label={t(`bookings.status.${statusKey(s)}`)}
                onClick={() => toggleStatus(s)}
              />
            ))}
          </fieldset>

          <fieldset
            className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}
          >
            <legend className={css({ fontSize: "xs", color: "fg.muted", pr: "2" })}>
              {t("bookings.field.channel")}
            </legend>
            {BOOKING_CHANNELS.map((c) => (
              <Chip
                key={c}
                active={filter.channel.includes(c)}
                label={t(`bookings.channel.${c}`)}
                onClick={() => toggleChannel(c)}
              />
            ))}
          </fieldset>

          <div
            className={css({ display: "flex", gap: "3", flexWrap: "wrap", alignItems: "flex-end" })}
          >
            <label className={fieldLabel}>
              {t("bookings.filter.partyMin")}
              <input
                type="number"
                min={1}
                className={`${textInput} ${css({ w: "20" })}`}
                value={filter.partyMin ?? ""}
                onChange={(e) =>
                  onChange({
                    ...filter,
                    partyMin: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />
            </label>
            <label className={fieldLabel}>
              {t("bookings.filter.partyMax")}
              <input
                type="number"
                min={1}
                className={`${textInput} ${css({ w: "20" })}`}
                value={filter.partyMax ?? ""}
                onChange={(e) =>
                  onChange({
                    ...filter,
                    partyMax: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />
            </label>
            <button
              type="button"
              className={secondaryButton}
              onClick={() => onChange({ status: [], channel: [] })}
            >
              {t("bookings.filter.clear")}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
}

function Chip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={css({
        px: "2.5",
        py: "1",
        rounded: "full",
        borderWidth: "1px",
        fontSize: "xs",
        fontWeight: "medium",
        bg: active ? "colorPalette.default" : "bg.default",
        color: active ? "colorPalette.fg" : "fg.default",
        borderColor: active ? "colorPalette.default" : "border.default",
      })}
    >
      {label}
    </button>
  );
}

/** Map an enum value to its i18n label-key suffix (NO_SHOW → noShow). */
function statusKey(s: BookingStatus): string {
  return s === "NO_SHOW" ? "noShow" : s.toLowerCase();
}
